import React, { memo, useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { Button, Icon } from '@sravni/react-design-system';
import { ThumbUp } from '@sravni/react-icons';
import { useBoolean } from '@sravni/react-utils';

import { IAnalyticsObject, sendLikeReviewEvent, sendUnlikeReviewEvent } from '../../helpers/analyticsEvents';
import { IVotes } from '../../interfaces/reviews';
import { useFingerprint } from '../../store/hooks/users';
import { doVoteReview } from '../../store/reducers/reviews';

import styles from './likes.module.scss';

interface IProps {
  reviewId: number;
  votes: IVotes;
  isUserVoted: boolean;
  analyticLabel?: IAnalyticsObject;
}

export const Likes: React.FC<IProps> = memo(({ reviewId, votes, isUserVoted, analyticLabel }) => {
  const dispatch = useDispatch();
  const fp = useFingerprint();
  const [isLiked, setIsLiked] = useBoolean(isUserVoted);
  const [votesCount, setVotesCount] = useState(votes?.positive);

  useEffect(() => {
    if (isUserVoted) {
      setIsLiked.on();
    } else {
      setIsLiked.off();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUserVoted]);

  useEffect(() => {
    const sumOfVotes = votes.positive - votes.negative;
    let userAction;
    if (isUserVoted && isLiked) {
      // юзер не убирал свой лайк
      userAction = 0;
    } else if (isUserVoted && !isLiked) {
      // юзер убрал лайк
      userAction = -1;
    } else if (!isUserVoted && isLiked) {
      // юзер лайкнул
      userAction = 1;
    } else if (!isUserVoted && !isLiked) {
      // юзер не лайкал
      userAction = 0;
    }

    setVotesCount(sumOfVotes + userAction);
  }, [isLiked, isUserVoted, votes]);

  const handleVote = useCallback(
    (vote: boolean) => {
      dispatch(
        doVoteReview({
          reviewId: reviewId,
          positive: vote,
          fingerPrint: fp,
        }),
      );
    },
    [dispatch, reviewId, fp],
  );

  const handleLikeClick = (e: React.MouseEvent<HTMLButtonElement | HTMLDivElement>) => {
    e.preventDefault();

    handleVote(!isLiked);

    if (isLiked) {
      sendUnlikeReviewEvent(analyticLabel);
    } else {
      sendLikeReviewEvent(analyticLabel);
    }

    setIsLiked.toggle();
  };

  return (
    <Button onClick={handleLikeClick} size={36} variant={'outlined'} color={'dark'} className={styles.button}>
      {votesCount > 0 && votesCount}
      <Icon className={styles.icon} size={20} icon={<ThumbUp className={isLiked ? styles.iconFilled : ''} />} />
    </Button>
  );
});
